import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { Spinner } from 'Components/common/Spinner';
import { Dialog, PageHeader } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import { getVendorById, getVendorAssessments } from 'services/vendor-service';
import { VendorForm } from './VendorForm';
import { formatDateToDateBR } from 'utils/date_utils';
import { Main as Container } from 'Components/Main';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BORDER = 'var(--vo-cloud)';
const LABEL_COLOR = 'var(--vo-ink-2)';
const SUCCESS = 'var(--vo-success)';
const ERROR = 'var(--vo-danger)';
const WARNING = 'var(--vo-warning)';
const LIGHT_BG = 'var(--vo-mist)';

const TIER_STYLES = {
  '1': { color: ERROR,   bg: 'var(--vo-danger-10)'    },
  '2': { color: WARNING, bg: 'var(--vo-warning-10)'    },
  '3': { color: SUCCESS, bg: 'var(--vo-success-10)'   },
};

const TIER_DEFAULT_MONTHS = { '1': 6, '2': 12, '3': 24 };

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') return val.en || val.pt || val.es || Object.values(val)[0] || ''
  return String(val)
}

const EditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: var(--vo-capri-50); }
`;

/* ── Layout ───────────────────────────────────────── */
const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const DetailCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const DetailCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${BORDER};
`;

/* ── Info card ────────────────────────────────────── */
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FullCol = styled.div`
  grid-column: 1 / -1;
`;

const DetailLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`;

const DetailValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  margin: 0;
`;

const DetailValueDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${LABEL_COLOR};
  margin: 0;
  line-height: 1.65;
`;

/* ── Risk card ────────────────────────────────────── */
const RiskSection = styled.div`
  margin-bottom: 14px;
`;

const TierBadge = styled.span`
  display: inline-block;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $tier }) => TIER_STYLES[$tier]?.color || MUTED};
  background: ${({ $tier }) => TIER_STYLES[$tier]?.bg || LIGHT_BG};
  padding: 4px 12px;
  border-radius: 20px;
`;

const SubText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 4px 0 0;
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
`;

const ScoreNum = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${LABEL_COLOR};
  margin: 0;
`;

const RiskScoreBox = styled.div`
  background: ${LIGHT_BG};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
`;

const RiskScoreNum = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: ${({ $tier }) => TIER_STYLES[$tier]?.color || MUTED};
  margin: 0;
  letter-spacing: -.02em;
`;

const HRule = styled.div`
  height: 1px;
  background: ${BORDER};
  margin-bottom: 14px;
`;

const DpaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $ok }) => $ok ? SUCCESS : MUTED};
  background: ${({ $ok }) => $ok ? 'var(--vo-success-10)' : LIGHT_BG};
  padding: 4px 12px;
  border-radius: 20px;
`;

const RecertBadge = styled.span`
  display: inline-block;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $overdue }) => $overdue ? ERROR : SUCCESS};
  background: ${({ $overdue }) => $overdue ? 'var(--vo-danger-10)' : 'var(--vo-success-10)'};
  padding: 4px 12px;
  border-radius: 20px;
`;

/* ── Timeline ─────────────────────────────────────── */
const TimelineWrap = styled.div`
  position: relative;
  padding-left: 32px;
  &::before {
    content: '';
    position: absolute;
    left: 12px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: ${BORDER};
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 24px;
  &:last-child { padding-bottom: 0; }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -26px;
  top: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $status }) =>
    $status === 'Approved'              ? SUCCESS :
    $status === 'Rejected'              ? ERROR   :
    $status === 'UnderReview'           ? WARNING :
    $status === 'PendingVendorResponse' ? BLUE    :
    'var(--vo-steel)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const TimelineDate = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0 0 4px;
`;

const TimelineCard = styled.div`
  background: white;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 12px 16px;
`;

const TimelineFramework = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 4px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'Approved'    ? 'var(--vo-success-10)'  :
    $status === 'Rejected'    ? 'var(--vo-danger-10)'   :
    $status === 'UnderReview' ? 'var(--vo-warning-10)'   :
    LIGHT_BG};
  color: ${({ $status }) =>
    $status === 'Approved'    ? SUCCESS :
    $status === 'Rejected'    ? ERROR   :
    $status === 'UnderReview' ? WARNING :
    MUTED};
`;

const ScoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $score }) =>
    $score >= 90 ? 'var(--vo-success-10)' :
    $score >= 70 ? 'var(--vo-warning-10)'  :
    'var(--vo-danger-10)'};
  color: ${({ $score }) =>
    $score >= 90 ? SUCCESS :
    $score >= 70 ? WARNING :
    ERROR};
`;

const ViewDetailsBtn = styled.button`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  color: ${BLUE};
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 0 0;
  display: block;
  &:hover { text-decoration: underline; }
`;

/* ── Loading / empty states ───────────────────────── */
const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${MUTED};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const FullLoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const ErrorWrap = styled.div`
  padding: 32px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${ERROR};
`;

/* ── SVGs ─────────────────────────────────────────── */

const EditSVG = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const CheckCircleSVG = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const TimelineIconSVG = ({ status }) => {
  if (status === 'Approved') return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
  if (status === 'Rejected') return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1"/>
    </svg>
  );
};

/* ── Component ────────────────────────────────────── */
export const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['vendor', 'common']);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: vendorData, isLoading: vendorLoading } = useQuery(
    ['vendor', id],
    () => getVendorById(id),
    { enabled: !!id }
  );

  const { data: assessmentsData, isLoading: assessmentsLoading } = useQuery(
    ['vendor-assessments', id],
    () => getVendorAssessments(id),
    { enabled: !!id }
  );

  const vendor = vendorData?.data?.success ? vendorData.data.data : null;
  const assessments = assessmentsData?.data?.data || [];

  if (vendorLoading) {
    return (
      <FullLoadingWrap>
        <Spinner $size={24} $borderColor="var(--vo-line)" $speed="0.6s" />
      </FullLoadingWrap>
    );
  }

  if (!vendor) {
    return <ErrorWrap>{t('vendor:detail.notFound')}</ErrorWrap>;
  }

  const tier = String(vendor.tier);
  const effectiveMonths = vendor.recertificationPeriodMonths
    || vendor.effectiveRecertificationPeriodMonths
    || TIER_DEFAULT_MONTHS[tier];

  return (
    <Container>
      <PageHeader
        title={vendor.name}
        segments={[
          { label: t('common:breadcrumbs.thirdPartyRiskManagement') },
          { label: t('vendor:form.breadcrumb.vendors'), route: '/vendors' },
          { label: vendor.name },
        ]}
        actions={
          <EditBtn onClick={() => setIsEditOpen(true)}>
            <EditSVG />
            {t('vendor:form.title.edit')}
          </EditBtn>
        }
      />

      {/* Two-column grid */}
      <DetailGrid>
        {/* Vendor info card */}
        <DetailCard>
          <DetailCardTitle>{t('vendor:detail.sections.info')}</DetailCardTitle>
          <InfoGrid>
            <div>
              <DetailLabel>{t('vendor:detail.fields.contactEmail')}</DetailLabel>
              <DetailValue>{vendor.contactEmail}</DetailValue>
            </div>
            <div>
              <DetailLabel>{t('vendor:detail.fields.contactName')}</DetailLabel>
              <DetailValue>{vendor.contactName || t('vendor:detail.notSpecified')}</DetailValue>
            </div>
            <FullCol>
              <DetailLabel>{t('vendor:detail.fields.relationshipDescription')}</DetailLabel>
              <DetailValueDesc>{vendor.relationshipDescription || t('vendor:detail.noDescription')}</DetailValueDesc>
            </FullCol>
            <div>
              <DetailLabel>{t('vendor:detail.fields.createdDate')}</DetailLabel>
              <DetailValue style={{ color: LABEL_COLOR }}>{formatDateToDateBR(vendor.createdAt)}</DetailValue>
            </div>
            <div>
              <DetailLabel>{t('vendor:detail.fields.lastUpdated')}</DetailLabel>
              <DetailValue style={{ color: LABEL_COLOR }}>{formatDateToDateBR(vendor.updatedAt)}</DetailValue>
            </div>
          </InfoGrid>
        </DetailCard>

        {/* Risk & Compliance card */}
        <DetailCard>
          <DetailCardTitle>{t('vendor:detail.sections.risk')}</DetailCardTitle>

          <RiskSection>
            <DetailLabel>{t('vendor:detail.fields.riskTier')}</DetailLabel>
            <TierBadge $tier={tier}>
              {t(`vendor:tier.${tier}.fullLabel`, { defaultValue: t('vendor:tier.unknown.fullLabel') })}
            </TierBadge>
            <SubText>{t(`vendor:tier.${tier}.description`, { defaultValue: '' })}</SubText>
          </RiskSection>

          <ScoreGrid>
            <div>
              <DetailLabel>{t('vendor:detail.fields.dataSensitivity')}</DetailLabel>
              <ScoreNum>{vendor.dataSensitivity} {t('vendor:form.riskScore.outOf')}</ScoreNum>
            </div>
            <div>
              <DetailLabel>{t('vendor:detail.fields.businessCriticality')}</DetailLabel>
              <ScoreNum>{vendor.businessCriticality} {t('vendor:form.riskScore.outOf')}</ScoreNum>
            </div>
          </ScoreGrid>

          <RiskScoreBox>
            <DetailLabel>{t('vendor:detail.fields.inherentRiskScore')}</DetailLabel>
            <RiskScoreNum $tier={tier}>{vendor.inherentRiskScore} {t('vendor:form.riskScore.outOf')}</RiskScoreNum>
          </RiskScoreBox>

          <HRule />

          <RiskSection>
            <DetailLabel>{t('vendor:detail.fields.dpaStatus')}</DetailLabel>
            <DpaBadge $ok={vendor.dpaEstablished}>
              {vendor.dpaEstablished && <CheckCircleSVG />}
              {vendor.dpaEstablished ? t('vendor:detail.dpa.established') : t('vendor:detail.dpa.none')}
            </DpaBadge>
          </RiskSection>

          <RiskSection style={{ marginBottom: 0 }}>
            <DetailLabel>{t('vendor:detail.fields.recertificationPeriod')}</DetailLabel>
            {vendor.recertificationDueDate && (
              <RecertBadge $overdue={vendor.recertificationDue}>
                {formatDateToDateBR(vendor.recertificationDueDate)}
              </RecertBadge>
            )}
            <SubText>
              {t('vendor:detail.recertification.months', { count: effectiveMonths })}
              {' '}
              {vendor.recertificationPeriodMonths
                ? t('vendor:detail.recertification.customOverride')
                : t('vendor:detail.recertification.tierDefaultLabel')}
            </SubText>
          </RiskSection>
        </DetailCard>
      </DetailGrid>

      {/* Assessment history — full width below grid */}
      <DetailCard>
        <DetailCardTitle>{t('vendor:detail.sections.assessmentHistory')}</DetailCardTitle>
        {assessmentsLoading ? (
          <LoadingWrap>
            <Spinner $size={24} $borderColor="var(--vo-line)" $speed="0.6s" />
          </LoadingWrap>
        ) : assessments.length === 0 ? (
          <EmptyState>{t('vendor:detail.noAssessments')}</EmptyState>
        ) : (
          <TimelineWrap>
            {assessments.map((assessment) => (
              <TimelineItem key={assessment._id}>
                <TimelineDot $status={assessment.status}>
                  <TimelineIconSVG status={assessment.status} />
                </TimelineDot>
                <TimelineDate>{formatDateToDateBR(assessment.createdAt)}</TimelineDate>
                <TimelineCard>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <TimelineFramework>{str(assessment.frameworkName)}</TimelineFramework>
                      <StatusBadge $status={assessment.status}>
                        {t(`vendor:assessmentStatus.${assessment.status}`, { defaultValue: assessment.status })}
                      </StatusBadge>
                    </div>
                    {assessment.trustScore && (
                      <ScoreBadge $score={assessment.trustScore}>
                        {assessment.trustScore}%
                      </ScoreBadge>
                    )}
                  </div>
                  <ViewDetailsBtn onClick={() => navigate(`/vendor-assessments/${assessment._id}`)}>
                    {t('vendor:detail.viewDetails')} →
                  </ViewDetailsBtn>
                </TimelineCard>
              </TimelineItem>
            ))}
          </TimelineWrap>
        )}
      </DetailCard>

      <Dialog.custom
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title={t('vendor:form.title.edit')}
      >
        <VendorForm
          vendor={vendor}
          onSuccess={() => {
            setIsEditOpen(false);
            queryClient.invalidateQueries(['vendor', id]);
          }}
          onCancel={() => setIsEditOpen(false)}
        />
      </Dialog.custom>
    </Container>
  );
};
